// Spend approvals (SPEC §19, 2026-10-01). The spend owner (PJ) sees every request: waiting ones first, with who asked,
// what, when, the estimate and the budget, and Approve (optionally with a lower budget) / Decline (with a note); the
// history below. Everyone else sees their own requests and can cancel one that is still waiting.
import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { SpendRequestView } from '../../../src/shared/api-team';
import { api, errorMessage } from '../api';
import { Empty, ErrorBanner, fmtDateTime, timeAgo, toast } from '../components/ui';
import { Field, inputValue, useLoad } from '../admin/ui';
import { refreshApprovals, useApprovals } from '../approvals';
import { navigate } from '../app';
import '../admin/admin.css';

const usd = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `$${n.toFixed(2)}`);

const STATUS_CHIP: Record<SpendRequestView['status'], { cls: string; label: string }> = {
  pending: { cls: 'chip chip-warn', label: 'waiting' },
  approved: { cls: 'chip chip-ok', label: 'approved' },
  declined: { cls: 'chip chip-bad', label: 'declined' },
  canceled: { cls: 'chip', label: 'canceled' },
};

/** The few facts worth a glance, per kind (the summary sentence carries the rest). */
function facts(r: SpendRequestView): string[] {
  const d = r.detail;
  const out: string[] = [];
  if (r.kind === 'lead_search') {
    if (Array.isArray(d.areas)) out.push(`Areas: ${(d.areas as string[]).join(', ')}`);
    if (Array.isArray(d.terms)) out.push(`Terms: ${(d.terms as string[]).join(', ')}`);
    if (d.placesLow !== null && d.placesLow !== undefined) out.push(`About ${String(d.placesLow)}–${String(d.placesHigh)} places`);
    if (typeof d.monthSpentUsd === 'number' && typeof d.monthCapUsd === 'number') out.push(`Month so far: $${d.monthSpentUsd.toFixed(2)} of $${d.monthCapUsd.toFixed(2)} (at estimate time)`);
    if (d.confirmHandPick) out.push('Includes hand-pick-only states');
  }
  if (r.kind === 'research' && d.niche) out.push(`Profile: ${String(d.niche)}`);
  if (r.kind === 'niche_propose' && d.offerHint) out.push(`Offer hint: ${String(d.offerHint)}`);
  return out;
}

function resultLine(r: SpendRequestView): string | null {
  const x = r.result;
  if (!x) return null;
  if (typeof x.searchId === 'number') return `Search #${x.searchId} ${String(x.status ?? 'started')}${typeof x.budgetUsd === 'number' ? `, budget $${x.budgetUsd.toFixed(2)}` : ''}.`;
  if (typeof x.found === 'boolean') return x.found ? 'Email found and added to the drawer.' : 'Apollo had no email for this person.';
  if (typeof x.nicheKey === 'string') return `Draft profile ${String(x.displayName ?? x.nicheKey)} v${String(x.nicheVersion)} saved for review.`;
  return null;
}

function EmailLine(props: { r: SpendRequestView; isOwner: boolean }) {
  const r = props.r;
  const bits: ComponentChildren[] = [];
  if (r.ownerEmailError) bits.push(<span class="chip chip-bad" title={r.ownerEmailError}>Email to the owner failed: {r.ownerEmailError}</span>);
  if (r.decisionEmailError) bits.push(<span class="chip chip-bad" title={r.decisionEmailError}>Decision email failed: {r.decisionEmailError}</span>);
  return bits.length ? <div class="row" style="flex-wrap:wrap;gap:6px">{bits}</div> : null;
}

function PendingCard(props: { r: SpendRequestView; isOwner: boolean; ownerFirst: string; highlight: boolean; onDone: () => void }) {
  const r = props.r;
  const [budget, setBudget] = useState(r.budgetUsd !== null ? r.budgetUsd.toFixed(2) : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'' | 'approve' | 'decline' | 'cancel'>('');
  const [error, setError] = useState<string | null>(null);
  const editableBudget = props.isOwner && r.kind === 'lead_search' && r.budgetUsd !== null;

  const act = async (what: 'approve' | 'decline' | 'cancel') => {
    setBusy(what); setError(null);
    try {
      if (what === 'approve') {
        const b = Number(budget);
        const body: Record<string, unknown> = { note: note.trim() || undefined };
        if (editableBudget && Number.isFinite(b) && b > 0 && Math.abs(b - (r.budgetUsd ?? 0)) > 0.004) body.budgetUsd = b;
        await api.post(`/api/approvals/${r.id}/approve`, body);
        toast(`Approved #${r.id}: ${r.kindLabel} for ${r.requestedByName}.`, 'ok');
      } else if (what === 'decline') {
        await api.post(`/api/approvals/${r.id}/decline`, { note: note.trim() || undefined });
        toast(`Declined #${r.id}. Nothing was spent.`, 'ok');
      } else {
        await api.post(`/api/approvals/${r.id}/cancel`);
        toast(`Request #${r.id} canceled.`, 'ok');
      }
      void refreshApprovals();
      props.onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  };

  return (
    <div class="card stack" style={`gap:10px${props.highlight ? ';border-color:var(--ink);box-shadow:var(--shadow-hover)' : ''}`} id={`req-${r.id}`}>
      <div class="row" style="justify-content:space-between;flex-wrap:wrap;gap:8px">
        <div class="row" style="gap:8px;flex-wrap:wrap">
          <span class="chip chip-info">{r.kindLabel}</span>
          <b>#{r.id}</b>
          <span>asked by <b>{r.requestedByName}</b></span>
          <span class="muted" title={r.requestedAt}>{fmtDateTime(r.requestedAt)} · {timeAgo(r.requestedAt)}</span>
        </div>
        <div class="row" style="gap:14px">
          <span><span class="eyebrow">Estimate</span> <b>{usd(r.estimateUsd)}</b></span>
          <span><span class="eyebrow">Budget</span> <b>{r.budgetUsd === null ? 'n/a' : `up to ${usd(r.budgetUsd)}`}</b></span>
        </div>
      </div>
      <div style="font-size:15px">{r.summary}</div>
      {facts(r).length ? <ul class="muted" style="margin:0;padding-left:18px;font-size:13px">{facts(r).map((f) => <li>{f}</li>)}</ul> : null}
      <EmailLine r={r} isOwner={props.isOwner} />
      {props.isOwner ? (
        <div class="row" style="gap:10px;flex-wrap:wrap;align-items:flex-end">
          {editableBudget ? (
            <Field label="Budget (USD)" hint="You can lower it, not raise it." style="max-width:150px">
              <input type="number" min={0.01} step={0.01} max={r.requestedBudgetUsd ?? r.budgetUsd ?? undefined} value={budget} onInput={(e) => setBudget(inputValue(e))} />
            </Field>
          ) : null}
          <Field label="Note (optional; sent to the requester)" style="flex:1;min-width:240px">
            <input value={note} maxLength={500} onInput={(e) => setNote(inputValue(e))} placeholder={`e.g. "Approved; keep it to Hamilton for now"`} />
          </Field>
          <button class="btn btn-primary" disabled={busy !== ''} onClick={() => void act('approve')}>{busy === 'approve' ? 'Approving…' : 'Approve and run'}</button>
          <button class="btn btn-danger" disabled={busy !== ''} onClick={() => void act('decline')}>{busy === 'decline' ? 'Declining…' : 'Decline'}</button>
        </div>
      ) : (
        <div class="row" style="justify-content:space-between;flex-wrap:wrap;gap:8px">
          <span class="chip chip-warn">Waiting for {props.ownerFirst}'s approval. Nothing has been spent.</span>
          <button class="btn btn-sm" disabled={busy !== ''} onClick={() => void act('cancel')}>{busy === 'cancel' ? 'Canceling…' : 'Cancel request'}</button>
        </div>
      )}
      <ErrorBanner error={error} />
    </div>
  );
}

export function Approvals(_props: { params: Record<string, string> }) {
  const appr = useApprovals();
  const isOwner = !!appr?.isOwner;
  const ownerFirst = (appr?.ownerName ?? 'PJ').split(' ')[0];
  const list = useLoad(() => api.get<SpendRequestView[]>('/api/approvals?status=all&limit=200'), []);
  const focusId = Number(new URLSearchParams(location.search).get('id')) || null;
  useEffect(() => {
    const t = setInterval(() => { void list.reload(); }, 30_000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusId && list.data) document.getElementById(`req-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [focusId, !!list.data]);

  const rows = list.data ?? [];
  const pending = rows.filter((r) => r.status === 'pending');
  const history = rows.filter((r) => r.status !== 'pending');

  return (
    <div class="adm-page" style="max-width:1200px">
      <div class="adm-head">
        <div>
          <h1>Approvals</h1>
          <div class="adm-sub">
            {isOwner
              ? 'Every lead-engine spend by anyone else waits here for you: who asked, what, when, the estimate and the budget. Nothing is spent until you approve it.'
              : `Lead searches, research and other paid lead-engine work go to ${appr?.ownerName ?? 'PJ'} first. Your requests and their decisions are below; you also get an email.`}
          </div>
        </div>
        <button class="btn btn-sm" onClick={() => { void list.reload(); void refreshApprovals(); }} disabled={list.loading}>Refresh</button>
      </div>
      <ErrorBanner error={list.error} />
      {list.loading && !list.data ? <div class="muted">Loading requests…</div> : null}

      <h2 style="margin:4px 0 0">Waiting {pending.length ? <span class="chip chip-warn">{pending.length}</span> : null}</h2>
      {list.data && !pending.length ? <Empty>{isOwner ? 'Nothing is waiting for you.' : 'You have no requests waiting.'}</Empty> : null}
      {pending.map((r) => <PendingCard key={r.id} r={r} isOwner={isOwner} ownerFirst={ownerFirst} highlight={r.id === focusId} onDone={() => void list.reload()} />)}

      <h2 style="margin:8px 0 0">History</h2>
      {list.data && !history.length ? <div class="muted">No decisions yet.</div> : null}
      {history.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr><th>#</th><th>Asked</th><th>Who</th><th>What</th><th class="num">Estimate</th><th class="num">Budget</th><th>Decision</th><th>Note</th><th>Result</th></tr></thead>
            <tbody>{history.map((r) => (
              <tr id={`req-${r.id}`} style={r.id === focusId ? 'background:var(--sand)' : undefined}>
                <td class="num">{r.id}</td>
                <td title={r.requestedAt}>{fmtDateTime(r.requestedAt)}</td>
                <td>{r.requestedByName}</td>
                <td style="max-width:420px"><span class="chip chip-info">{r.kindLabel}</span><div style="font-size:13px;margin-top:4px">{r.summary}</div><EmailLine r={r} isOwner={isOwner} /></td>
                <td class="num">{usd(r.estimateUsd)}</td>
                <td class="num">{usd(r.budgetUsd)}{r.requestedBudgetUsd !== null && r.budgetUsd !== null && r.requestedBudgetUsd !== r.budgetUsd ? <div class="muted" style="font-size:12px">asked {usd(r.requestedBudgetUsd)}</div> : null}</td>
                <td><span class={STATUS_CHIP[r.status].cls}>{STATUS_CHIP[r.status].label}</span><div class="muted" style="font-size:12px">{r.decidedByName ?? ''}{r.decidedAt ? ` · ${fmtDateTime(r.decidedAt)}` : ''}</div></td>
                <td style="max-width:240px">{r.decisionNote ?? <span class="muted">—</span>}</td>
                <td style="max-width:240px;font-size:13px">
                  {resultLine(r) ?? <span class="muted">—</span>}
                  {r.kind === 'lead_search' && typeof r.result?.searchId === 'number'
                    ? <div><a href={`/lead-search/searches`} onClick={(e) => { e.preventDefault(); navigate('/lead-search/searches'); }}>Open searches</a></div> : null}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
